/**
 * Web MIDI polyfill backed by our native CoreMIDI plugin.
 *
 * Why: Apple hasn't shipped the Web MIDI API in WKWebView despite
 * iOS/iPadOS having CoreMIDI since iOS 4. That means our existing
 * `useWebMidi` hook (which calls `navigator.requestMIDIAccess`) sees
 * `undefined` inside the Capacitor shell — no inputs, no outputs, no
 * sending.
 *
 * How: we write a native Capacitor plugin (NativeMidi.swift) that
 * wraps CoreMIDI, then in this file we install a shim that exposes
 * the exact same `navigator.requestMIDIAccess` surface the Web MIDI
 * spec defines — but backed by the native plugin instead of WebKit.
 * Consumers (MidiRouter, useWebMidi, sequencer output routing, etc.)
 * don't need any changes — they keep calling `navigator.requestMIDIAccess`
 * and get a MIDIAccess-shaped object that works.
 *
 * Scope: we implement just enough of the Web MIDI spec for our use
 * cases — enumeration, `onstatechange`, `onmidimessage`, `send()`.
 * Not implemented: SysEx, timestamp scheduling beyond "send now",
 * `MIDIAccess.sysexEnabled`/`close()`/`clear()`. These can be added
 * to the plugin as needs arise.
 *
 * No-op on web (where `navigator.requestMIDIAccess` already exists,
 * or doesn't exist but wiring up CoreMIDI in a non-native context
 * doesn't make sense).
 */

import {
  Capacitor,
  registerPlugin,
  type PluginListenerHandle,
} from '@capacitor/core'
import { isNativePlatform } from '@/lib/nativePlatform'

interface NativeMidiDeviceInfo {
  id: string
  name: string
  manufacturer: string
  state: 'connected' | 'disconnected'
}

interface NativeMidiPlugin {
  start(): Promise<void>
  getDevices(): Promise<{
    inputs: NativeMidiDeviceInfo[]
    outputs: NativeMidiDeviceInfo[]
  }>
  send(options: {
    id: string
    data: number[]
    timestamp?: number
  }): Promise<void>
  /** Presents Apple's system BLE MIDI central (scan/pair) picker. */
  showBluetoothPairing(): Promise<void>
  /** Presents Apple's system BLE MIDI peripheral (advertise) picker. */
  showBluetoothAdvertising(): Promise<void>
  /** Toggle Network MIDI (RTP-MIDI over Bonjour). */
  setNetworkMidiEnabled(options: { enabled: boolean }): Promise<{
    enabled: boolean
    networkName: string
    localName: string
    /** Populated if requested=true but iOS refused to enable. */
    error?: string
  }>
  /** Read current Network MIDI state. */
  getNetworkMidiStatus(): Promise<{
    enabled: boolean
    networkName: string
    localName: string
    contacts: number
    connections: number
  }>
  addListener(
    event: 'stateChange',
    cb: () => void,
  ): Promise<PluginListenerHandle>
  addListener(
    event: 'message',
    cb: (payload: { id: string; data: number[]; timestamp: number }) => void,
  ): Promise<PluginListenerHandle>
}

const NativeMidi = registerPlugin<NativeMidiPlugin>('NativeMidi')

/**
 * Shared state for the single `MIDIAccess` instance the Web MIDI spec
 * allows per page. We build one on the first `requestMIDIAccess()`
 * call and return the same object on subsequent calls — mirroring
 * WebKit/Chromium behavior.
 */
let accessInstance: NativeMidiAccess | null = null
let accessPromise: Promise<MIDIAccess> | null = null

// ── Minimal MIDIPort/Input/Output classes ─────────────────────────

type PortKind = 'input' | 'output'

class NativeMidiPort {
  readonly id: string
  readonly type: PortKind
  name: string | undefined
  manufacturer: string | undefined
  state: 'connected' | 'disconnected'
  connection: 'open' | 'closed' | 'pending' = 'closed'
  version: string = '1.0'
  onstatechange: ((event: MIDIConnectionEvent) => void) | null = null

  constructor(info: NativeMidiDeviceInfo, type: PortKind) {
    this.id = info.id
    this.name = info.name
    this.manufacturer = info.manufacturer
    this.state = info.state
    this.type = type
  }

  applyUpdate(info: NativeMidiDeviceInfo) {
    this.name = info.name
    this.manufacturer = info.manufacturer
    this.state = info.state
  }

  async open(): Promise<MIDIPort> {
    this.connection = 'open'
    return this as unknown as MIDIPort
  }

  async close(): Promise<MIDIPort> {
    this.connection = 'closed'
    return this as unknown as MIDIPort
  }
}

class NativeMidiInput extends NativeMidiPort {
  onmidimessage: ((event: MIDIMessageEvent) => void) | null = null

  constructor(info: NativeMidiDeviceInfo) {
    super(info, 'input')
  }

  deliver(data: number[], timestamp: number) {
    if (!this.onmidimessage) return
    const event = {
      data: new Uint8Array(data),
      timeStamp: timestamp,
      // Additional props some callers expect from MIDIMessageEvent
      // (Event base). We only populate what `useWebMidi.ts` reads.
      target: this as unknown,
    } as unknown as MIDIMessageEvent
    try {
      this.onmidimessage(event)
    } catch (err) {
      console.error('[nativeMidi] onmidimessage handler threw:', err)
    }
  }
}

class NativeMidiOutput extends NativeMidiPort {
  constructor(info: NativeMidiDeviceInfo) {
    super(info, 'output')
  }

  send(data: number[] | Uint8Array, timestamp?: number): void {
    // Convert to a plain number[] because Capacitor's bridge doesn't
    // serialize typed arrays natively.
    const payload = Array.isArray(data) ? data : Array.from(data)
    void NativeMidi.send({ id: this.id, data: payload, timestamp })
  }

  clear(): void {
    // No-op — we don't queue scheduled sends natively.
  }
}

// ── MIDIAccess ────────────────────────────────────────────────────

class NativeMidiAccess {
  readonly inputs: Map<string, NativeMidiInput> = new Map()
  readonly outputs: Map<string, NativeMidiOutput> = new Map()
  sysexEnabled: boolean = false
  onstatechange: ((event: MIDIConnectionEvent) => void) | null = null

  async init(): Promise<void> {
    await NativeMidi.start()

    // Subscribe BEFORE first enumeration so hotplug notifications
    // that fire between `start()` and `getDevices()` aren't lost.
    await NativeMidi.addListener('stateChange', () => {
      void this.refreshDevices()
    })
    await NativeMidi.addListener('message', ({ id, data, timestamp }) => {
      const input = this.inputs.get(id)
      if (input) input.deliver(data, timestamp)
    })

    await this.refreshDevices(/* suppressStateChange */ true)
  }

  private async refreshDevices(suppressStateChange = false): Promise<void> {
    const { inputs, outputs } = await NativeMidi.getDevices()
    // Diff + update so existing NativeMidiInput/Output instances keep
    // their onmidimessage/onstatechange callbacks attached across
    // hotplug refreshes.
    const changed = mergeDevices(this.inputs, inputs, NativeMidiInput)
    const changedOut = mergeDevices(this.outputs, outputs, NativeMidiOutput)
    if (!suppressStateChange && (changed || changedOut) && this.onstatechange) {
      try {
        this.onstatechange({} as MIDIConnectionEvent)
      } catch (err) {
        console.error('[nativeMidi] onstatechange handler threw:', err)
      }
    }
  }
}

/**
 * Merge a list of device infos into an existing ordered Map, preserving
 * instances when the id hasn't changed so that per-port callbacks
 * (onmidimessage) survive hotplugs. Returns true if the map changed
 * (added/removed/state flip).
 */
function mergeDevices<T extends NativeMidiPort>(
  target: Map<string, T>,
  fresh: NativeMidiDeviceInfo[],
  Ctor: new (info: NativeMidiDeviceInfo) => T,
): boolean {
  let changed = false
  const freshIds = new Set<string>()
  for (const info of fresh) {
    freshIds.add(info.id)
    const existing = target.get(info.id)
    if (existing) {
      if (existing.state !== info.state || existing.name !== info.name) {
        existing.applyUpdate(info)
        changed = true
      }
    } else {
      target.set(info.id, new Ctor(info))
      changed = true
    }
  }
  for (const id of Array.from(target.keys())) {
    if (!freshIds.has(id)) {
      target.delete(id)
      changed = true
    }
  }
  return changed
}

// ── Polyfill installation ─────────────────────────────────────────

/**
 * Install the CoreMIDI-backed `navigator.requestMIDIAccess`
 * implementation. Call from `main.tsx` BEFORE any hook that reads
 * `navigator.requestMIDIAccess` runs — specifically before the first
 * render of any component using `useWebMidi`. Safe to call multiple
 * times (idempotent).
 *
 * No-op on:
 *   - the web build (the browser's own Web MIDI is either present or
 *     genuinely unavailable; we don't second-guess)
 *   - non-native contexts where `Capacitor.isNativePlatform()` is
 *     false (SSR, tests)
 *   - platforms where the `NativeMidi` plugin isn't registered (an
 *     Android build in the future would need its own native plugin;
 *     until then we fail gracefully)
 */
export function installNativeMidiPolyfill(): void {
  if (!isNativePlatform()) return
  if (!Capacitor.isPluginAvailable('NativeMidi')) return
  if (typeof navigator === 'undefined') return

  // Don't clobber a real `requestMIDIAccess` if one ever appears
  // (future-proofing: if Apple ever ships Web MIDI in WKWebView,
  // defer to theirs).
  if (
    'requestMIDIAccess' in navigator &&
    typeof (navigator as Navigator).requestMIDIAccess === 'function'
  ) {
    return
  }

  ;(
    navigator as Navigator & {
      requestMIDIAccess: (options?: MIDIOptions) => Promise<MIDIAccess>
    }
  ).requestMIDIAccess = async (_options?: MIDIOptions): Promise<MIDIAccess> => {
    if (accessPromise) return accessPromise
    accessPromise = (async () => {
      const instance = new NativeMidiAccess()
      await instance.init()
      accessInstance = instance
      return instance as unknown as MIDIAccess
    })()
    return accessPromise
  }
}

/**
 * Exported for testing / diagnostics. Returns the live MIDIAccess
 * instance (or null if `requestMIDIAccess` hasn't been called yet).
 */
export function getNativeMidiAccess(): NativeMidiAccess | null {
  return accessInstance
}

// ── Extended (native-only) MIDI capabilities ─────────────────────
//
// These helpers wrap the transport-specific pairing UIs CoreMIDI
// exposes: Bluetooth LE MIDI (scan/pair/advertise) and Network
// MIDI (RTP-MIDI over Bonjour). There's no Web MIDI API equivalent,
// so we surface them through dedicated functions instead of trying
// to shoehorn them into `MIDIAccess`. Callers should feature-detect
// via `isNativeMidiAvailable()` before rendering UI to trigger them.

/**
 * True when the CoreMIDI-backed native plugin is available — i.e.,
 * we're in the Capacitor iOS shell on iPhone/iPad/Mac Catalyst and
 * the plugin loaded. Use this to gate UI affordances like
 * "Bluetooth MIDI" / "Network MIDI" buttons that only make sense
 * on native builds.
 */
export function isNativeMidiAvailable(): boolean {
  return isNativePlatform() && Capacitor.isPluginAvailable('NativeMidi')
}

/**
 * Present iOS's system picker for Bluetooth LE MIDI devices. User
 * scans, taps to pair, iOS handles the BLE dance. Paired devices
 * then appear automatically in the next `getDevices()` result and
 * in our Web MIDI-compatible `MIDIAccess.inputs/outputs` maps.
 */
export async function showBluetoothMidiPairing(): Promise<void> {
  if (!isNativeMidiAvailable()) return
  await NativeMidi.showBluetoothPairing()
}

/**
 * Present iOS's peripheral-mode picker so this device advertises as
 * a BLE MIDI peripheral. Other devices (a Mac, another iPad) can
 * then discover and connect TO this device — useful for "send my
 * sequencer to a paired laptop without a cable".
 */
export async function showBluetoothMidiAdvertising(): Promise<void> {
  if (!isNativeMidiAvailable()) return
  await NativeMidi.showBluetoothAdvertising()
}

/**
 * Toggle Network MIDI (RTP-MIDI over Bonjour on the local network).
 * When enabled, peers on the same WiFi (a Mac running Logic / Audio
 * MIDI Setup, another iPad, an rtpMIDI-equipped Windows machine)
 * can discover and pair this device. Triggers iOS's local-network
 * permission prompt the first time it's enabled.
 */
export async function setNetworkMidiEnabled(enabled: boolean): Promise<{
  enabled: boolean
  networkName: string
  localName: string
  error?: string
}> {
  if (!isNativeMidiAvailable()) {
    return { enabled: false, networkName: '', localName: '' }
  }
  return NativeMidi.setNetworkMidiEnabled({ enabled })
}

/**
 * Read the current Network MIDI session state without toggling it.
 * `contacts` is the number of peers iOS knows about (whether or not
 * currently connected); `connections` is the number actively
 * exchanging MIDI right now.
 */
export async function getNetworkMidiStatus(): Promise<{
  enabled: boolean
  networkName: string
  localName: string
  contacts: number
  connections: number
}> {
  if (!isNativeMidiAvailable()) {
    return {
      enabled: false,
      networkName: '',
      localName: '',
      contacts: 0,
      connections: 0,
    }
  }
  return NativeMidi.getNetworkMidiStatus()
}

// Install at module-import time so the polyfill is in place before
// stores that read `'requestMIDIAccess' in navigator` (like midiStore)
// evaluate their initial values. Idempotent — the function short-
// circuits on subsequent calls.
installNativeMidiPolyfill()
